import { lazy, Suspense, type ComponentType } from "react";
import { Route, Routes } from "react-router-dom";
import {
  AdminGuard,
  AuthGuard,
  GuestGuard,
  RoleGuard,
} from "./components/guards";
import { AdminLayout, LearnerLayout, PublicLayout } from "./components/layouts";
import { Spinner } from "./components/ui";
import { useAuth } from "./auth/AuthProvider";

function page<T extends Record<string, unknown>, K extends keyof T>(
  loader: () => Promise<T>,
  name: K,
) {
  return lazy(async () => ({
    default: (await loader())[name] as ComponentType,
  }));
}

const auth = () => import("./pages/AuthPages");
const learning = () => import("./pages/LearningPages");
const admin = () => import("./pages/AdminPages");
const editor = () => import("./pages/AdminCourseEditor");

const LoginPage = page(auth, "LoginPage");
const ForgotPasswordPage = page(auth, "ForgotPasswordPage");
const ResetPasswordPage = page(auth, "ResetPasswordPage");
const ActivatePage = page(auth, "ActivatePage");
const LegalPage = lazy(async () => ({ default: (await auth()).LegalPage }));
const ChangePasswordPage = page(auth, "ChangePasswordPage");
const MfaOnboardingPage = page(auth, "MfaOnboardingPage");
const AccountDeletionPage = page(auth, "AccountDeletionPage");
const ForbiddenPage = page(auth, "ForbiddenPage");
const NotFoundPage = page(auth, "NotFoundPage");
const HomePage = page(learning, "HomePage");
const CourseCatalogPage = page(learning, "CourseCatalogPage");
const CourseDetailPage = page(learning, "CourseDetailPage");
const LessonPlayerPage = page(learning, "LessonPlayerPage");
const ProfilePage = page(learning, "ProfilePage");
const AdminDashboardPage = page(admin, "AdminDashboardPage");
const AdminCoursesPage = page(admin, "AdminCoursesPage");
const AdminCourseReadOnlyPage = page(admin, "AdminCourseReadOnlyPage");
const AdminAnnouncementsPage = page(admin, "AdminAnnouncementsPage");
const AdminUsersPage = page(admin, "AdminUsersPage");
const AdminAnalyticsPage = page(admin, "AdminAnalyticsPage");
const AdminAuditPage = page(admin, "AdminAuditPage");
const AdminSystemPage = page(admin, "AdminSystemPage");
const NewCoursePage = page(editor, "NewCoursePage");
const AdminCourseEditorPage = page(editor, "AdminCourseEditorPage");

function AdminCourseRoute() {
  const { user } = useAuth();
  return user?.role === "SUPPORT" ? (
    <AdminCourseReadOnlyPage />
  ) : (
    <AdminCourseEditorPage />
  );
}

function PageLoading() {
  return (
    <div className="fullscreen-state">
      <Spinner label="正在打开页面" />
    </div>
  );
}

export function AppRoutes() {
  return (
    <Suspense fallback={<PageLoading />}>
      <Routes>
        <Route element={<PublicLayout />}>
          <Route element={<GuestGuard />}>
            <Route path="/login" element={<LoginPage />} />
            <Route path="/forgot-password" element={<ForgotPasswordPage />} />
            <Route path="/reset-password" element={<ResetPasswordPage />} />
            <Route path="/activate" element={<ActivatePage />} />
            <Route path="/invite/:token" element={<ActivatePage />} />
          </Route>
          <Route path="/privacy" element={<LegalPage type="privacy" />} />
          <Route path="/terms" element={<LegalPage type="terms" />} />
          <Route path="/account-deletion" element={<AccountDeletionPage />} />
        </Route>
        <Route element={<AuthGuard />}>
          <Route
            path="/change-password"
            element={
              <div className="public-layout">
                <main id="main-content">
                  <ChangePasswordPage />
                </main>
              </div>
            }
          />
          <Route
            path="/mfa-setup"
            element={
              <div className="public-layout">
                <main id="main-content">
                  <MfaOnboardingPage />
                </main>
              </div>
            }
          />
          <Route element={<LearnerLayout />}>
            <Route index element={<HomePage />} />
            <Route path="/courses" element={<CourseCatalogPage />} />
            <Route path="/courses/:courseId" element={<CourseDetailPage />} />
            <Route path="/profile" element={<ProfilePage />} />
            <Route path="/profile/security" element={<ProfilePage />} />
            <Route
              path="/learn/:courseId/lessons/:lessonId"
              element={<LessonPlayerPage />}
            />
            <Route path="/forbidden" element={<ForbiddenPage />} />
          </Route>
          <Route element={<AdminGuard />}>
            <Route element={<AdminLayout />}>
              <Route element={<RoleGuard allowed={["OWNER", "ADMIN"]} />}>
                <Route path="/admin" element={<AdminDashboardPage />} />
                <Route path="/admin/courses/new" element={<NewCoursePage />} />
                <Route
                  path="/admin/announcements"
                  element={<AdminAnnouncementsPage />}
                />
                <Route
                  path="/admin/analytics"
                  element={<AdminAnalyticsPage />}
                />
                <Route path="/admin/system" element={<AdminSystemPage />} />
              </Route>
              <Route path="/admin/courses" element={<AdminCoursesPage />} />
              <Route path="/admin/courses/:id" element={<AdminCourseRoute />} />
              <Route path="/admin/users" element={<AdminUsersPage />} />
              <Route path="/admin/audit" element={<AdminAuditPage />} />
            </Route>
          </Route>
        </Route>
        <Route path="*" element={<NotFoundPage />} />
      </Routes>
    </Suspense>
  );
}
